import styled from 'styled-components';
import TextInfoBox from './TextInfoBox';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { userState } from '../atoms';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isMobile } from '../utils/checkMobile';
import { InputTable } from '../assets/styles/InputTable';
import IssueGuide from './IssueGuide';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../services/_common';
import Portal from './Portal';
import GroupChkModal from './Modal/GroupChkModal';
import { v4 } from 'uuid';
import { toast } from 'react-toastify';

const Root = styled.div`
  .t-line-wrap {
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0;

    .t-line-container {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      width: 50%;

      .t-line-left {
        width: 100%;
      }

      .t-line-head {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .tag-head {
          padding: 0.125rem 0.75rem;
          border-radius: 5px;
          font-size: 0.875rem;
          color: #fff;

          &.reference {
            background: var(--filled-violet);
          }

          &.report {
            background: var(--filled-yellow);
          }

          &.approval {
            background: var(--filled-green);
          }
        }
      }

      .t-line-body {
        .tbody {
          height: 288px;
          overflow-y: auto;
          background: var(--tonal);

          .tr {
            background: var(--board);
          }
        }
      }

      &.t-report {
        .tbody {
          background: var(--tonal-amber);
        }
      }

      &.t-approval {
        .tbody {
          background: var(--tonal-green);
        }
      }

      &.t-reference {
        .tbody {
          background: var(--tonal-violet);
        }
      }
    }

    @media screen and (max-width: 768px) {
      & {
        flex-wrap: wrap;
        gap: 1rem;
      }

      .t-line-container {
        width: 100%;
      }
    }
  }

  .btn-right {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 4px;
    cursor: pointer;

    &.add-button {
      background-color: var(--filled-slate-deep);
      color: var(--board);

      &:hover {
        outline: 0.25rem solid var(--tonal-deep);
        &:active {
          transform: scale(0.95);
        }
      }
    }

    &.arrow-button {
      background-color: var(--filled-slate-deep);
      color: var(--board);

      &:hover {
        outline: 0.25rem solid var(--tonal-deep);
        &:active {
          transform: scale(0.95);
        }
      }
    }

    &.remove-button {
      background-color: var(--filled-red);
      color: var(--board);

      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 0, 'opsz' 24;
        font-size: 1rem;
      }

      &:hover {
        outline: 0.25rem solid var(--tonal-light-red);
        &:active {
          transform: scale(0.95);
        }
      }
    }
  }
`;

interface IUser {
  key: string;
  name: string;
}

interface IUserPart {
  grCd: string;
  grName: string;
  subList: IUser[];
}

interface ISelectedKey {
  [key: string]: string | null;
}

interface Props {
  gubun: string;
  list1: string[];
  list2: string[];
  setList1: Dispatch<SetStateAction<string[]>>;
  setList2: Dispatch<SetStateAction<string[]>>;
  selectedKey: ISelectedKey;
  setSelectedKey: Dispatch<SetStateAction<ISelectedKey>>;
}

const DraggableRow = ({ type, key, el, auth, index, moveRow, tableState, setTableState, onClickRow, selectedKey, cUserList }: any) => {
  const trRef = useRef(null);
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const newCdArray = tableState.map((elParam: any) => elParam.cdSort);
  const cdSortArray = newCdArray.sort((a: number, b: number) => a - b);

  const onClickDelete = () => {
    const tableArray = [...tableState];
    tableArray.splice(index, 1);
    setTableState(tableArray);
  };

  const renderName = (userId: string) => {
    const filterUser = cUserList.find((item: any) => item?.userId === userId);
    return `${filterUser?.userName}/${filterUser?.partName}`;
  };

  const [, drop] = useDrop({
    accept: 'ROW',
    hover(item: any, monitor) {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      moveRow(tableState, setTableState, dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item, monitor) => {
      // const copyArray = [...tableState];
      // const newArray = copyArray.map((cpItem, cpIndex) => ({ ...cpItem, cdSort: cdSortArray[cpIndex], editor: userInfo.userId }));
      // setTableState(newArray);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(trRef));

  return (
    <div ref={trRef} style={{ opacity: isDragging ? 0 : 1 }} role='presentation' onClick={() => onClickRow(el)}>
      <div className={`tr ${selectedKey && el === selectedKey ? 'selected' : ''}`} key={el}>
        <div className='trCol2p5 flex-center'>{index + 1}</div>
        <div className='flex-center content-overflow'>{renderName(el)}</div>
      </div>
    </div>
  );
};

const dragRow = (list: any[], setList: Dispatch<SetStateAction<any[]>>, fromIndex: number, toIndex: number) => {
  const updatedData = [...list];
  const [movedItem] = updatedData.splice(fromIndex, 1);
  updatedData.splice(toIndex, 0, movedItem);
  setList(updatedData);
};

const ApprovalLineTable = ({ gubun, list1, list2, setList1, setList2, selectedKey, setSelectedKey }: Props) => {
  const userInfo = useRecoilValue(userState);
  const { t } = useTranslation();
  const mobile = isMobile();
  const Backend = mobile ? TouchBackend : HTML5Backend;

  // const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // const [selectedKey2, setSelectedKey2] = useState<string | null>(null);
  const [cUserList, setCUserList] = useState<IUserPart[]>([]);
  const [userPartList, setUserPartList] = useState<IUserPart[]>([]);
  const [openModal, setOpenModal] = useState<any>({ type: '', status: false });
  const { data: cUserApi } = useQuery(['/user/company'], () => apiGet({ path: '/user/company', req: { hCd: userInfo?.hCd } }));
  const { data: userPartApi } = useQuery(['/user/company/part'], () => apiGet({ path: '/user/company/part', req: { hCd: userInfo?.hCd } }));

  useEffect(() => {
    cUserApi && setCUserList(cUserApi?.data?.data);
  }, [cUserApi]);

  useEffect(() => {
    if (userPartApi) {
      // 자신이 속한 userId 제외하기
      const newFilter = userPartApi?.data?.data?.map((el: any) => ({
        ...el,
        users: el.users.filter((el2: any) => el2.userId !== userInfo.userId),
        subList: el.users.filter((el2: any) => el2.userId !== userInfo.userId),
      }));
      setUserPartList(
        newFilter.map((el: any) => ({
          grCd: v4(),
          grName: el.partName,
          subList: el.users.map((el2: any) => ({ key: el2.userId, name: el2.userName })),
        }))
      );
    }
  }, [userPartApi]);

  const onClickRow = (key: string) => {
    setSelectedKey((prev: ISelectedKey) => ({ ...prev, selKey: key }));
  };

  const onClickRow2 = (key: string) => {
    setSelectedKey((prev: ISelectedKey) => ({ ...prev, selKey2: key }));
  };

  const onClickUp = (type: number) => {
    type === 1 ? listSpliceFn('up', selectedKey.selKey, list1, setList1) : listSpliceFn('up', selectedKey.selKey2, list2, setList2);
  };

  const onClickDown = (type: number) => {
    type === 1 ? listSpliceFn('down', selectedKey.selKey, list1, setList1) : listSpliceFn('down', selectedKey.selKey2, list2, setList2);
  };

  const onClickRemove = (type: number) => {
    type === 1 ? listSpliceFn('delete', selectedKey.selKey, list1, setList1, 'selKey', setSelectedKey) : listSpliceFn('delete', selectedKey.selKey2, list2, setList2, 'selKey2', setSelectedKey);
  };

  const listSpliceFn = (
    mode: 'up' | 'down' | 'delete',
    currentData: string | null,
    listData: string[],
    set: Dispatch<SetStateAction<string[]>>,
    keyName?: string,
    setKey?: Dispatch<SetStateAction<ISelectedKey>>
  ) => {
    const findIdx = listData.findIndex((el: string) => el === currentData);
    const newFilter = listData.filter((el: string) => el !== currentData);

    if (currentData !== null) {
      if (mode === 'up') {
        if (findIdx - 1 >= 0) {
          newFilter.splice(findIdx - 1, 0, currentData);
          set(newFilter);
        } else {
          toast.warn('선택된 행이 맨위 입니다');
        }
      }
      if (mode === 'down') {
        if (findIdx + 2 <= listData.length) {
          newFilter.splice(findIdx + 1, 0, currentData);
          set(newFilter);
        } else {
          toast.warn('선택된 행이 맨아래 입니다');
        }
      }
      if (mode === 'delete') {
        set(newFilter);
        setKey && setKey((prev: ISelectedKey) => ({ ...prev, [keyName || '']: null }));
      }
    } else {
      toast.warn('선택된 행이 없습니다');
    }
  };

  // 보고인 경우
  if (gubun === '1') {
    return (
      <DndProvider backend={Backend} options={{ enableMouseEvents: true }}>
        <Root>
          <TextInfoBox
            description='1. 드래그 & 드롭으로 보고서 라인을 변경할 수 있습니다.<br />
                        2. 보고서 라인은 총 6개까지만 설정할 수 있습니다.'
            color='orange'
            type='fill'
          />
          <div className='flex-basic t-line-wrap'>
            <div className='t-line-container t-report'>
              <div className='t-line-left'>
                <div className='t-line-head'>
                  <span className='tag-head report'>{t('보고')}</span>
                  <div
                    className='button add-button'
                    onClick={() => {
                      setOpenModal({ status: true, type: 'chipType', state: list1, el: list1 });
                    }}
                    role='button'
                    tabIndex={0}
                  >
                    <span className='material-symbols-rounded'>add</span>
                  </div>
                </div>
                <div className='t-line-body'>
                  <InputTable>
                    <div className='thead'>
                      <div className='tr'>
                        <div className='trCol2p5 flex-center'>No</div>
                        <div className='flex-center content-overflow'>{t('사원명/부서명')}</div>
                      </div>
                    </div>
                    {list1.length > 0 ? (
                      <div className='table'>
                        <div className='tbody'>
                          {list1.map((el, i) => (
                            <DraggableRow
                              type='bsfList'
                              key={el}
                              index={i}
                              el={el}
                              moveRow={dragRow}
                              tableState={list1}
                              setTableState={setList1}
                              onClickRow={onClickRow}
                              selectedKey={selectedKey.selKey}
                              cUserList={cUserList}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ position: 'sticky', left: 0, height: '100%' }}>
                        <IssueGuide />
                      </div>
                    )}
                  </InputTable>
                </div>
              </div>
              <div className='t-line-right'>
                <div className='btn-right'>
                  <div className='button arrow-button' onClick={() => onClickUp(1)} role='presentation'>
                    <span className='material-symbols-rounded'>keyboard_arrow_up</span>
                  </div>
                  <div className='button arrow-button' onClick={() => onClickDown(1)} role='presentation'>
                    <span className='material-symbols-rounded'>keyboard_arrow_down</span>
                  </div>
                  <div className='button remove-button' onClick={() => onClickRemove(1)} role='presentation'>
                    <span className='material-symbols-rounded'>delete</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Root>

        <Portal openModal={openModal?.status}>
          {openModal && openModal.type === 'chipType' && (
            <GroupChkModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              title={t('사용자 선택')}
              type='unit'
              selectedProps={list1}
              setSelectedProps={setList1}
              comparisonList={userPartList}
              maxLength={6}
            />
          )}
        </Portal>
      </DndProvider>
    );
  }

  // 결재인 경우
  return (
    <DndProvider backend={Backend} options={{ enableMouseEvents: true }}>
      <Root>
        <TextInfoBox
          description='1. 순서(No)의 제일 하단이 최종 결재자입니다.<br />2. 드래그 & 드롭으로 결재 라인을 변경할 수 있습니다.<br />3. 결재 라인은 총 6개까지만 설정할 수 있습니다.'
          color='orange'
          type='fill'
        />
        <div className='flex-basic t-line-wrap'>
          <div className='t-line-container t-approval'>
            <div className='t-line-left'>
              <div className='t-line-head'>
                <span className='tag-head approval'>{t('결재')}</span>
                <div
                  className='button add-button'
                  onClick={() => {
                    setOpenModal({ status: true, type: 'chipType1' });
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <span className='material-symbols-rounded'>add</span>
                </div>
              </div>
              <div className='t-line-body'>
                <InputTable>
                  <div className='thead'>
                    <div className='tr'>
                      <div className='trCol2p5 flex-center'>No</div>
                      <div className='flex-center content-overflow'>{t('사원명/부서명')}</div>
                    </div>
                  </div>
                  {list1.length > 0 ? (
                    <div className='table'>
                      <div className='tbody'>
                        {list1.map((el, i) => (
                          <DraggableRow
                            type='bsfList'
                            key={el}
                            index={i}
                            el={el}
                            moveRow={dragRow}
                            tableState={list1}
                            setTableState={setList1}
                            onClickRow={onClickRow}
                            selectedKey={selectedKey.selKey}
                            cUserList={cUserList}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ position: 'sticky', left: 0, height: '100%' }}>
                      <IssueGuide />
                    </div>
                  )}
                </InputTable>
              </div>
            </div>
            <div className='t-line-right'>
              <div className='btn-right'>
                <div className='button arrow-button' onClick={() => onClickUp(1)} role='presentation'>
                  <span className='material-symbols-rounded'>keyboard_arrow_up</span>
                </div>
                <div className='button arrow-button' onClick={() => onClickDown(1)} role='presentation'>
                  <span className='material-symbols-rounded'>keyboard_arrow_down</span>
                </div>
                <div className='button remove-button' onClick={() => onClickRemove(1)} role='presentation'>
                  <span className='material-symbols-rounded'>delete</span>
                </div>
              </div>
            </div>
          </div>

          <div className='t-line-container t-reference'>
            <div className='t-line-left'>
              <div className='t-line-head'>
                <span className='tag-head reference'>{t('수신 및 참조')}</span>
                <div
                  className='button add-button'
                  onClick={() => {
                    setOpenModal({ status: true, type: 'chipType2' });
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <span className='material-symbols-rounded'>add</span>
                </div>
              </div>
              <div className='t-line-body'>
                <InputTable>
                  <div className='thead'>
                    <div className='tr'>
                      <div className='trCol2p5 flex-center'>No</div>
                      <div className='flex-center content-overflow'>{t('사원명/부서명')}</div>
                    </div>
                  </div>
                  {list2.length > 0 ? (
                    <div className='table'>
                      <div className='tbody'>
                        {list2.map((el, i) => (
                          <DraggableRow
                            type='bsfList'
                            key={el}
                            index={i}
                            el={el}
                            moveRow={dragRow}
                            tableState={list2}
                            setTableState={setList2}
                            onClickRow={onClickRow2}
                            selectedKey={selectedKey.selKey2}
                            cUserList={cUserList}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ position: 'sticky', left: 0, height: '100%' }}>
                      <IssueGuide />
                    </div>
                  )}
                </InputTable>
              </div>
            </div>
            <div className='t-line-right'>
              <div className='btn-right'>
                <div className='button arrow-button' onClick={() => onClickUp(2)} role='presentation'>
                  <span className='material-symbols-rounded'>keyboard_arrow_up</span>
                </div>
                <div className='button arrow-button' onClick={() => onClickDown(2)} role='presentation'>
                  <span className='material-symbols-rounded'>keyboard_arrow_down</span>
                </div>
                <div className='button remove-button' onClick={() => onClickRemove(2)} role='presentation'>
                  <span className='material-symbols-rounded'>delete</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Root>

      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'chipType1' && (
          <GroupChkModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            title={t('사용자 선택')}
            type='unit'
            selectedProps={list1}
            setSelectedProps={setList1}
            comparisonList={userPartList}
            maxLength={6}
          />
        )}
        {openModal && openModal.type === 'chipType2' && (
          <GroupChkModal openModal={openModal} setOpenModal={setOpenModal} title={t('사용자 선택')} type='unit' selectedProps={list2} setSelectedProps={setList2} comparisonList={userPartList} />
        )}
      </Portal>
    </DndProvider>
  );
};

export default ApprovalLineTable;
